import { unwrap } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import { Badge, Button, EmptyState, ErrorState, Icon, Skeleton, toast, type IconName } from "@sagot/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useErrorMessage } from "../../../../lib/api-errors";
import { usePermissions } from "../../../../session/permissions";
import { useApi } from "../../../../session/session-provider";
import { useFormatters, wsKeys, type SessionOut } from "../../shared";
import { useSessions } from "../use-settings";

const MOBILE_UA = /iphone|android|mobile|ipad/i;
/** Long-lived accounts collect many sessions; the current one first, then the most recent few. */
const VISIBLE_SESSIONS = 8;

export function orderSessions<T extends { current: boolean; last_active_at: string }>(sessions: readonly T[]): T[] {
  return [...sessions].sort((a, b) => Number(b.current) - Number(a.current) || b.last_active_at.localeCompare(a.last_active_at));
}

/** "Chrome on Windows" from a user agent when the API has no device label. */
export function describeAgent(userAgent: string | null): string | null {
  if (!userAgent) return null;
  const browser = /edg\//i.test(userAgent) ? "Edge" : /firefox/i.test(userAgent) ? "Firefox" : /chrome|crios/i.test(userAgent) ? "Chrome" : /safari/i.test(userAgent) ? "Safari" : null;
  const os = /windows/i.test(userAgent) ? "Windows" : /iphone|ipad|ios/i.test(userAgent) ? "iOS" : /android/i.test(userAgent) ? "Android" : /mac os/i.test(userAgent) ? "macOS" : /linux/i.test(userAgent) ? "Linux" : null;
  if (browser && os) return `${browser} · ${os}`;
  return browser ?? os;
}

function deviceIcon(session: SessionOut): IconName {
  return MOBILE_UA.test(session.user_agent ?? session.device ?? "") ? "smartphone" : "monitor";
}

function SessionRow({ session, onRevoke, busy, readOnly }: { session: SessionOut; onRevoke: (id: string) => void; busy: boolean; readOnly: boolean }) {
  const { t } = useTranslation();
  const format = useFormatters();
  const label = session.device ?? describeAgent(session.user_agent) ?? t("settings.sessions.unknownDevice");
  const meta = [session.location, session.ip, session.current ? t("settings.sessions.activeNow") : format.relative(session.last_active_at)].filter(Boolean).join(" · ");
  return (
    <li className="flex items-center gap-3 px-5 py-3">
      <Icon name={deviceIcon(session)} className="size-5 text-fg-subtle" />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold">
          {label}
          {session.current ? (
            <Badge tone="success" className="ml-2">
              {t("settings.sessions.thisDevice")}
            </Badge>
          ) : null}
        </div>
        <div className="text-xs text-fg-subtle">{meta}</div>
      </div>
      {session.current ? null : (
        <Button variant="secondary" size="sm" disabled={readOnly || busy} onClick={() => { onRevoke(session.id); }} aria-label={t("settings.sessions.revokeOne", { device: label })}>
          {t("settings.sessions.revoke")}
        </Button>
      )}
    </li>
  );
}

/** Active console sessions of the signed-in member: revoke one or all others. */
export function SessionsCard() {
  const { t } = useTranslation();
  const api = useApi();
  const queryClient = useQueryClient();
  const message = useErrorMessage();
  const readOnly = usePermissions().readOnly;
  const sessions = useSessions();
  const refresh = () => queryClient.invalidateQueries({ queryKey: wsKeys.sessions });
  const revoke = useMutation({
    mutationFn: (id: string) => unwrap(api.DELETE("/v1/me/sessions/{session_id}", { params: { path: { session_id: id } } })),
    onSuccess: async () => {
      toast.success(t("settings.sessions.revoked"));
      await refresh();
    },
    onError: (caught) => toast.error(message(caught)),
  });
  const revokeOthers = useMutation({
    mutationFn: () => unwrap(api.DELETE("/v1/me/sessions")),
    onSuccess: async () => {
      toast.success(t("settings.sessions.othersRevoked"));
      await refresh();
    },
    onError: (caught) => toast.error(message(caught)),
  });
  const [showAll, setShowAll] = useState(false);
  const ordered = orderSessions(sessions.data ?? []);
  const shown = showAll ? ordered : ordered.slice(0, VISIBLE_SESSIONS);
  const others = ordered.filter((s) => !s.current).length;
  return (
    <section className="card overflow-hidden" aria-labelledby="sessions-title">
      <div className="card-header">
        <h2 id="sessions-title" className="card-title">
          {t("settings.sessions.title")}
        </h2>
        <Button variant="ghost" size="sm" className="text-danger" disabled={readOnly || others === 0} loading={revokeOthers.isPending} onClick={() => { revokeOthers.mutate(); }}>
          {t("settings.sessions.revokeOthers")}
        </Button>
      </div>
      {sessions.isPending ? (
        <ul className="divide-y divide-line" aria-hidden="true">
          {[0, 1, 2].map((i) => (
            <li key={i} className="flex items-center gap-3 px-5 py-3">
              <Skeleton className="size-5" />
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-4 w-[220px]" />
                <Skeleton className="h-3 w-[280px] max-w-full" />
              </div>
            </li>
          ))}
        </ul>
      ) : sessions.isError ? (
        <ErrorState headingLevel={3} title={t("settings.sessions.loadError")} onRetry={() => void sessions.refetch()} retryLabel={t("common.retry")} />
      ) : sessions.data.length === 0 ? (
        <EmptyState icon="monitor" headingLevel={3} title={t("settings.sessions.emptyTitle")} description={t("settings.sessions.emptyBody")} />
      ) : (
        <ul className="divide-y divide-line" data-testid="sessions">
          {shown.map((session) => (
            <SessionRow key={session.id} session={session} onRevoke={(id) => { revoke.mutate(id); }} busy={revoke.isPending} readOnly={readOnly} />
          ))}
        </ul>
      )}
      {ordered.length > VISIBLE_SESSIONS ? (
        <div className="flex justify-center border-t border-line px-5 py-3">
          <Button variant="ghost" size="sm" aria-expanded={showAll} onClick={() => { setShowAll((v) => !v); }}>
            {showAll ? t("settings.sessions.showFewer") : t("settings.sessions.showAll", { count: ordered.length })}
          </Button>
        </div>
      ) : null}
    </section>
  );
}
